import { useState, useEffect, useRef, useCallback } from 'react';
import { VIEWS } from '../utils/appConstants';

// Detail views map to their parent list for fallback
const DETAIL_VIEWS = {
  [VIEWS.CUSTOMER_DETAILS]: VIEWS.CUSTOMERS,
  [VIEWS.JOB_DETAILS]: VIEWS.JOBS,
  [VIEWS.TREATMENT_REPORT_DETAILS]: VIEWS.TREATMENT_REPORTS,
};

const ENTITY_STORAGE_KEY = 'pestcrm_selectedEntity';

const getStoredEntity = () => {
  try {
    const raw = sessionStorage.getItem(ENTITY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
};

const getInitialView = () => {
  try {
    const saved = sessionStorage.getItem('pestcrm_currentView');
    if (saved && Object.values(VIEWS).includes(saved)) {
      if (DETAIL_VIEWS[saved]) {
        // Detail view — only restore if we have entity data, otherwise fall back
        return getStoredEntity() ? saved : DETAIL_VIEWS[saved];
      }
      return saved;
    }
  } catch (e) { /* ignore */ }
  return VIEWS.DASHBOARD;
};

export const useAppNavigation = () => {
  const [currentView, setCurrentView] = useState(getInitialView);
  const [selectedCustomer, setSelectedCustomer] = useState(() => {
    const view = getInitialView();
    return view === VIEWS.CUSTOMER_DETAILS ? getStoredEntity() : null;
  });
  const [selectedJob, setSelectedJob] = useState(() => {
    const view = getInitialView();
    return view === VIEWS.JOB_DETAILS ? getStoredEntity() : null;
  });
  const [selectedTreatmentReport, setSelectedTreatmentReport] = useState(() => {
    const view = getInitialView();
    return view === VIEWS.TREATMENT_REPORT_DETAILS ? getStoredEntity() : null;
  });
  const [preselectedCustomer, setPreselectedCustomer] = useState(null);
  const [previousView, setPreviousView] = useState(null);
  const [navigationContext, setNavigationContext] = useState(null);

  // Flag to prevent pushing history when handling popstate
  const isRestoringRef = useRef(false);

  // Replace the initial history entry on mount so there's a state to pop back to
  useEffect(() => {
    const initialView = getInitialView();
    const historyState = { view: initialView };
    if (DETAIL_VIEWS[initialView]) {
      const entity = getStoredEntity();
      if (entity) historyState.data = entity;
    }
    window.history.replaceState(historyState, '');
  }, []);

  // Persist current view to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('pestcrm_currentView', currentView);
    } catch (e) { /* ignore */ }
  }, [currentView]);

  // Keep entity storage in sync when selectedCustomer/Job/Report changes
  // (e.g. after an update refreshes the entity with fresh API data)
  useEffect(() => {
    try {
      if (currentView === VIEWS.CUSTOMER_DETAILS && selectedCustomer) {
        sessionStorage.setItem(ENTITY_STORAGE_KEY, JSON.stringify(selectedCustomer));
      } else if (currentView === VIEWS.JOB_DETAILS && selectedJob) {
        sessionStorage.setItem(ENTITY_STORAGE_KEY, JSON.stringify(selectedJob));
      } else if (currentView === VIEWS.TREATMENT_REPORT_DETAILS && selectedTreatmentReport) {
        sessionStorage.setItem(ENTITY_STORAGE_KEY, JSON.stringify(selectedTreatmentReport));
      }
    } catch (e) { /* ignore */ }
  }, [currentView, selectedCustomer, selectedJob, selectedTreatmentReport]);

  const handleNavigation = useCallback((view, data = null, context = null) => {
    // Store previous view before changing
    setPreviousView(currentView);
    setCurrentView(view);

    // Store navigation context for the target view
    setNavigationContext(context);

    // Handle preselected customer context
    if (context?.preselectedCustomer) {
      setPreselectedCustomer(context.preselectedCustomer);
    } else if (view !== VIEWS.INVOICES) {
      setPreselectedCustomer(null);
    }

    if (view === VIEWS.CUSTOMER_DETAILS && data) {
      setSelectedCustomer(data);
    } else if (view === VIEWS.JOB_DETAILS && data) {
      setSelectedJob(data);
    } else if (view === VIEWS.TREATMENT_REPORT_DETAILS && data) {
      setSelectedTreatmentReport(data);
    }

    // Persist or clear entity data for page refresh survival
    try {
      if (DETAIL_VIEWS[view] && data) {
        sessionStorage.setItem(ENTITY_STORAGE_KEY, JSON.stringify(data));
      } else {
        sessionStorage.removeItem(ENTITY_STORAGE_KEY);
      }
    } catch (e) { /* ignore */ }

    // Push to browser history (skip if we're restoring from popstate)
    if (!isRestoringRef.current) {
      const historyState = { view };
      if (data) historyState.data = data;
      if (context) historyState.context = context;
      window.history.pushState(historyState, '');
    }
  }, [currentView]);

  // Listen for browser back/forward buttons
  useEffect(() => {
    const handlePopstate = (event) => {
      const state = event.state;

      // No state means we've hit the bottom of the history stack —
      // stay on dashboard instead of leaving the app
      if (!state || !state.view) {
        window.history.pushState({ view: VIEWS.DASHBOARD }, '');
        setCurrentView(VIEWS.DASHBOARD);
        setPreselectedCustomer(null);
        return;
      }

      isRestoringRef.current = true;

      const { view, data, context } = state;

      // For detail views without data, fall back to parent list
      if (DETAIL_VIEWS[view] && !data) {
        setCurrentView(DETAIL_VIEWS[view]);
      } else {
        setCurrentView(view);

        // Restore entity data
        if (view === VIEWS.CUSTOMER_DETAILS && data) {
          setSelectedCustomer(data);
        } else if (view === VIEWS.JOB_DETAILS && data) {
          setSelectedJob(data);
        } else if (view === VIEWS.TREATMENT_REPORT_DETAILS && data) {
          setSelectedTreatmentReport(data);
        }
      }

      // Restore context
      if (context?.preselectedCustomer) {
        setPreselectedCustomer(context.preselectedCustomer);
      } else {
        setPreselectedCustomer(null);
      }

      isRestoringRef.current = false;
    };

    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, []);

  const clearSelections = () => {
    setSelectedCustomer(null);
    setSelectedJob(null);
    setSelectedTreatmentReport(null);
    setPreselectedCustomer(null);
    setPreviousView(null);
  };

  const goToPreviousView = () => {
    if (previousView) {
      const tempPrevious = previousView;
      setPreviousView(currentView);
      setCurrentView(tempPrevious);
    }
  };

  return {
    currentView,
    selectedCustomer,
    selectedJob,
    selectedTreatmentReport,
    preselectedCustomer,
    previousView,
    navigationContext,
    handleNavigation,
    goToPreviousView,
    clearSelections,
    setSelectedCustomer,
    setSelectedJob,
    setCurrentView,
    setPreselectedCustomer,
    setNavigationContext
  };
};
